<template>
  <div class="prepaid-page">
    <!-- 页面头部 -->
    <header class="page-header">
      <div class="header-badge">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/>
          <path d="M2 12l10 5 10-5"/>
        </svg>
        LayaAir MCP 服务
      </div>
      <h1 class="page-title">选择订阅方案</h1>
      <p class="page-subtitle">Layabox推出AI驱动的MCP（Model Context Protocol）服务，致力于为开发者提供智能化的LayaAir引擎开发支持。该服务有效的解决了通过大语言模型自动化编码过程中遇到的常见难题，例如：API接口调用规范、功能模块协同集成、技术方案实现路径等核心问题，全面提升开发效率与代码质量。选择最合适您的方案：</p>
    </header>

    <!-- 产品卡片列表 -->
    <div class="pricing-grid">
      <!-- 公测畅享期 -->
      <div class="pricing-card featured">
        <div class="card-badge">限时免费</div>
        <div class="card-header">
          <h3 class="card-title">公测畅享期</h3>
          <div class="card-price">
            <span class="price-amount">免费</span>
            <span class="price-period">截止 2026.01.31</span>
          </div>
        </div>
        <div class="card-features">
          <div class="feature-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            无限制调用次数
          </div>
          <div class="feature-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            全功能体验
          </div>
        </div>
        <button
          class="card-btn primary"
          :class="{ disabled: isNavigating }"
          :disabled="isNavigating"
          @click="gotoApiKey"
        >
          <span v-if="isNavigating" class="loading-spinner"></span>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
          </svg>
          立即体验
        </button>
      </div>

      <!-- 免费版 -->
      <div class="pricing-card">
        <div class="card-header">
          <h3 class="card-title">免费版</h3>
          <div class="card-price">
            <span class="price-currency">¥</span>
            <span class="price-amount">0</span>
            <span class="price-period">/月</span>
          </div>
        </div>
        <div class="card-features">
          <div class="feature-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            每日 10 次调用
          </div>
          <div class="feature-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            每日 0 点自动重置
          </div>
        </div>
        <button
          class="card-btn"
          :class="{ disabled: isNavigating }"
          :disabled="isNavigating"
          @click="gotoApiKey"
        >
          <span v-if="isNavigating" class="loading-spinner"></span>
          免费注册
        </button>
      </div>

      <!-- 动态产品列表 -->
      <div class="pricing-card" v-for="item in dataRef.productList" :key="item.product_id">
        <div class="card-header">
          <h3 class="card-title">{{ item.product_title }}</h3>
          <div class="card-price">
            <template v-if="showPrice === 'true'">
              <span class="price-currency">¥</span>
              <span class="price-amount">{{ toThousandFilter(item.product_discount / 100) }}</span>
            </template>
            <span v-else class="price-amount">¥--</span>
            <span class="price-period">/月</span>
          </div>
        </div>
        <div class="card-features" v-html="formatDesc(item.product_desc)"></div>
        <button
          class="card-btn"
          :class="{ disabled: disabledBuy === 'true' }"
          :disabled="disabledBuy === 'true'"
          @click="buy(item.product_id)"
        >
          <span v-if="isLoading" class="loading-spinner"></span>
          {{ disabledBuy === 'true' ? '敬请期待' : '获取' }}
        </button>
      </div>
    </div>
  </div>

  <AiCode ref="aiCodeRef" />

  <!-- 支付弹窗 -->
  <el-dialog
    v-model="dataRef.payDialogVisible"
    title="微信支付"
    width="320px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    class="pay-dialog"
  >
    <div class="pay-result" v-if="dataRef.payResult">
      <div v-if="dataRef.payResult == 4" class="result-success">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        <p>支付成功</p>
      </div>
      <div v-else class="result-fail">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <p>支付异常，请联系管理员</p>
      </div>
    </div>
    <div class="qr-container" v-else>
      <div class="qr-wrapper">
        <qrcode-vue :value="dataRef.codeUrl" :size="dataRef.codeSize" level="H" />
      </div>
      <p class="qr-tip">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        请使用微信扫码支付
      </p>
    </div>
  </el-dialog>
</template>

<script setup>
import AiCode from '@/components/AICode/index.vue'
import { getProductList, useCode } from '@/api/product'
import { createOrder, getOrderInfo } from '@/api/order'
import { onMounted, reactive, ref } from 'vue'
import QrcodeVue from 'qrcode.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { toThousandFilter } from '@/utils/index'
import { useRouter } from 'vue-router'

const router = useRouter()
const userStore = useUserStore()
const disabledBuy = ref(import.meta.env.VITE_DISABLE_BUY)
const showPrice = ref(import.meta.env.VITE_SHOW_PRICE)

onMounted(() => {
  list()
})

const dataRef = reactive({
  productList: [],
  codeUrl: '',
  payDialogVisible: false,
  codeSize: 220,
  payResult: '',
})

const isLoading = ref(false)
const isNavigating = ref(false) // 连点防护
const aiCodeRef = ref(null)

function list() {
  getProductList({
    service_id: 4,
    product_type: 1,
  }).then((res) => {
    dataRef.productList = res.data.data.list
  })
}

function formatDesc(desc) {
  // 转换产品描述为feature-item格式
  if (!desc) return ''

  const checkIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>'

  // 先解码转义的引号
  let html = desc.replace(/\\"/g, '"')

  // 使用临时容器解析HTML
  const items = []

  // 匹配 <p class="text">内容</p>
  html = html.replace(/<p\s+class="text">([^<]*)<\/p>/gi, (match, content) => {
    items.push(`<div class="feature-title">${content.trim()}</div>`)
    return ''
  })

  // 匹配 <p class="item">· 内容</p>
  html = html.replace(/<p\s+class="item">·?\s*([^<]*)<\/p>/gi, (match, content) => {
    items.push(`<div class="feature-item">${checkIcon}<span>${content.trim()}</span></div>`)
    return ''
  })

  // 处理剩余的独立文本（如 · 可购买额度包</p>）
  // 先移除可能残留的 </p>
  html = html.replace(/<\/p>/g, '')
  // 匹配 · 开头的独立文本
  html = html.replace(/·\s*([^<·]+)/g, (match, content) => {
    const trimmed = content.trim()
    if (trimmed) {
      items.push(`<div class="feature-item">${checkIcon}<span>${trimmed}</span></div>`)
    }
    return ''
  })

  return items.join('')
}

async function activeCode() {
  const isLogin = userStore.checkLogin()
  if (!isLogin) {
    userStore.login()
    return false
  }
  aiCodeRef.value.showDialog()
}

async function buy(product_id) {
  const isLogin = userStore.checkLogin()
  if (!isLogin) {
    userStore.login()
    return false
  }
  isLoading.value = true
  dataRef.payResult = null
  let createOrderRes = await createOrder({ product_id, pay_channel: 'wx_pub_qrpay' })
  if (createOrderRes.data.ret != 0) {
    ElMessage.error(createOrderRes.data.msg)
    isLoading.value = false
    return false
  }
  const codeUrl = createOrderRes.data.data.code_url
  dataRef.codeUrl = codeUrl.substr(codeUrl.indexOf('weixin://'))
  dataRef.payDialogVisible = true
  isLoading.value = false
  checkOrderStatus(createOrderRes.data.data.order_id)
}

function checkOrderStatus(orderId) {
  setTimeout(async function () {
    const res = await getOrderInfo({ order_id: orderId })
    if (res.data.data.order_status == 4) {
      ElMessage.success('支付成功')
      dataRef.payResult = res.data.data.order_status
      setTimeout(() => {
        dataRef.payDialogVisible = false
      }, 4000)
    } else if (res.data.data.order_status == 5) {
      dataRef.payResult = res.data.data.order_status
    } else {
      checkOrderStatus(orderId)
    }
  }, 2000)
}

async function gotoApiKey() {
  // 连点防护
  if (isNavigating.value) return
  isNavigating.value = true

  try {
    await router.replace({ name: 'userApiKeys' })
  } catch (error) {
    console.log("cancel login")
  } finally {
    // 延迟重置，防止快速连点
    setTimeout(() => {
      isNavigating.value = false
    }, 500)
  }
}
</script>

<style lang="scss" scoped>
// 设计变量
$bg-dark: #0b0d10;
$bg-card: #12151a;
$border-color: #2a2f38;
$text-primary: #f5f5f7;
$text-secondary: #9ca3af;
$text-muted: #6b7280;
$accent-gold: #c9a227;
$accent-gold-light: #e6c84a;
$accent-gold-dark: #a8871f; // darken($accent-gold, 10%)
$accent-green: #10b981;
$accent-red: #ef4444;

.prepaid-page {
  min-height: calc(100vh - 62px);
  padding: 48px 24px 64px;
  max-width: 1200px;
  margin: 0 auto;
}

// 页面头部
.page-header {
  text-align: center;
  margin-bottom: 56px;
  animation: fadeIn 0.6s ease-out;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba($accent-gold, 0.1);
  border: 1px solid rgba($accent-gold, 0.25);
  border-radius: 100px;
  font-size: 13px;
  font-weight: 500;
  color: $accent-gold;
  margin-bottom: 20px;

  svg {
    width: 16px;
    height: 16px;
  }
}

.page-title {
  font-size: 42px;
  font-weight: 700;
  background: linear-gradient(135deg, $accent-gold-light, $accent-gold, $accent-gold-dark);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 16px;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 16px;
  color: $text-secondary;
  margin: 0 auto;
  max-width: 700px;
  line-height: 1.7;
}

// 定价卡片网格
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  animation: slideUp 0.6s ease-out 0.1s backwards;
}

// 定价卡片
.pricing-card {
  position: relative;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 20px;
  padding: 32px 28px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: rgba($accent-gold, 0.3);
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  }

  &.featured {
    border-color: $accent-gold;
    background: linear-gradient(180deg, rgba($accent-gold, 0.08) 0%, $bg-card 50%);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 2px;
      background: linear-gradient(90deg, transparent, $accent-gold, transparent);
    }
  }
}

.card-badge {
  position: absolute;
  top: -12px;
  right: 24px;
  padding: 6px 14px;
  background: linear-gradient(135deg, $accent-gold, $accent-gold-dark);
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
  color: $bg-dark;
}

.card-header {
  margin-bottom: 24px;
}

.card-title {
  font-size: 22px;
  font-weight: 600;
  color: $text-primary;
  margin: 0 0 16px;
}

.card-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.price-currency {
  font-size: 20px;
  font-weight: 600;
  color: $text-primary;
}

.price-amount {
  font-size: 40px;
  font-weight: 700;
  color: $text-primary;
  line-height: 1;
}

.price-period {
  font-size: 14px;
  color: $text-muted;
  margin-left: 4px;
}

// 功能列表
.card-features {
  flex: 1;
  padding: 20px 0;
  border-top: 1px solid $border-color;
  margin-bottom: 24px;
}

:deep(.feature-title) {
  font-size: 12px;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
}

:deep(.feature-item) {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  font-size: 14px;
  color: $text-secondary;
  line-height: 1.5;

  svg {
    width: 18px;
    height: 18px;
    min-width: 18px;
    color: $accent-green;
    flex-shrink: 0;
    margin-top: 2px;
  }

  span {
    flex: 1;
  }
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  font-size: 14px;
  color: $text-secondary;
  line-height: 1.5;

  svg {
    width: 18px;
    height: 18px;
    min-width: 18px;
    color: $accent-green;
    flex-shrink: 0;
    margin-top: 2px;
  }
}

// 按钮
.card-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  color: $text-primary;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
  }

  &.primary {
    background: linear-gradient(135deg, $accent-gold, $accent-gold-dark);
    border: none;
    color: $bg-dark;

    &:hover {
      background: linear-gradient(135deg, $accent-gold-light, $accent-gold);
      transform: scale(1.02);
    }
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      transform: none;
      background: rgba(255, 255, 255, 0.08);
    }
  }
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

// 支付弹窗样式
:deep(.pay-dialog) {
  .el-dialog {
    border-radius: 16px;
    background: $bg-card;
  }

  .el-dialog__header {
    border-bottom: 1px solid $border-color;
    padding: 20px 24px;
  }

  .el-dialog__title {
    color: $text-primary;
    font-weight: 600;
  }

  .el-dialog__body {
    padding: 24px;
  }
}

.qr-container {
  text-align: center;
}

.qr-wrapper {
  display: inline-block;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
}

.qr-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  font-size: 14px;
  color: $text-secondary;

  svg {
    width: 20px;
    height: 20px;
    color: #07c160;
  }
}

.pay-result {
  text-align: center;
  padding: 20px 0;
}

.result-success,
.result-fail {
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
  }

  p {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
}

.result-success {
  svg { color: $accent-green; }
  p { color: $accent-green; }
}

.result-fail {
  svg { color: $accent-red; }
  p { color: $accent-red; }
}

// 动画
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// 响应式
@media (max-width: 768px) {
  .prepaid-page {
    padding: 32px 16px 48px;
  }

  .page-title {
    font-size: 32px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .pricing-card {
    padding: 24px 20px;
  }

  .price-amount {
    font-size: 32px;
  }
}
</style>
